import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { GripVertical, Edit2, Trash2, Plus, X, Star, CheckSquare, AlignLeft, List, Loader2, Check, Link2, ArrowLeft, SlidersHorizontal } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useFeedbackManagementStore from '@/stores/AdminStore/FeedbackManagementStore';
import { Question, FeedbackForm } from '@/services/PagesServices/FeedbackManagementService';
import LoadingSpinner from '@/Components/common/LoadingSpinner';
import ErrorDisplay from '@/Components/common/ErrorDisplay';
import './FormBuilder.css';

type QuestionType = 'short_text' | 'paragraph' | 'multiple_choice_single' | 'multiple_choice_multi' | 'star_rating' | 'slider';


interface LocalQuestion {
  id: string;
  type: QuestionType;
  text: string;
  required: boolean;
  options?: string[];
  correctAnswers?: number[];
  score?: number;
}

interface EditFormProps {
  onBack: () => void;
}

const QUESTION_TYPES: { value: QuestionType; label: string; icon: React.ReactNode }[] = [
  { value: 'short_text', label: 'Short Text', icon: <AlignLeft size={16} /> },
  { value: 'paragraph', label: 'Paragraph', icon: <AlignLeft size={16} /> },
  { value: 'multiple_choice_single', label: 'Multiple Choice (Single)', icon: <List size={16} /> },
  { value: 'multiple_choice_multi', label: 'Multiple Choice (Multi)', icon: <CheckSquare size={16} /> },
  { value: 'star_rating', label: 'Star Rating', icon: <Star size={16} /> },
  { value: 'slider', label: 'Slider', icon: <SlidersHorizontal size={16} /> },
];

const SortableEditQuestionItem: React.FC<{
  question: LocalQuestion;
  index: number;
  onEdit: (q: LocalQuestion) => void;
  onDelete: (id: string) => void;
  getTypeLabel: (type: QuestionType) => string;
}> = ({ question, index, onEdit, onDelete, getTypeLabel }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: question.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="admin-feedback-question-item">
      <div className="admin-feedback-question-drag" {...attributes} {...listeners} style={{ cursor: 'grab' }}>
        <GripVertical size={16} />
        <span className="admin-feedback-question-number">Q{index + 1}</span>
      </div>
      <div className="admin-feedback-question-content">
        <div className="admin-feedback-question-text">{question.text}</div>
        <div className="admin-feedback-question-meta">
          <span className="admin-feedback-question-type">{getTypeLabel(question.type)}</span>
          {question.required && <span className="admin-feedback-question-required">Required</span>}
        </div>
      </div>
      <div className="admin-feedback-question-actions">
        <button className="admin-feedback-icon-btn" onClick={() => onEdit(question)}>
          <Edit2 size={16} />
        </button>
        <button className="admin-feedback-icon-btn admin-feedback-icon-btn-delete" onClick={() => onDelete(question.id)}>
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

const EditForm: React.FC<EditFormProps> = ({ onBack }) => {
  const { currentForm, editingFormId, saving, loading, error, updateForm, clearCurrentForm } = useFeedbackManagementStore();

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formType, setFormType] = useState<'feedback' | 'satisfaction' | 'assessment'>('feedback');
  const [assessmentType, setAssessmentType] = useState<'objective' | 'subjective' | 'both'>('objective');
  const [showCorrectAnswers, setShowCorrectAnswers] = useState(false);
  const [showReport, setShowReport] = useState(true);
  const [objectiveRatio, setObjectiveRatio] = useState(50);
  const [expiryDate, setExpiryDate] = useState('');
  const [maxResponses, setMaxResponses] = useState('');
  const [onePerBrowser, setOnePerBrowser] = useState(false);
  const [collectName, setCollectName] = useState(false);
  const [collectEmail, setCollectEmail] = useState(false);
  const [questions, setQuestions] = useState<LocalQuestion[]>([]);

  // Modal state
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<LocalQuestion | null>(null);
  const [newQuestionType, setNewQuestionType] = useState<QuestionType>('short_text');
  const [newQuestionText, setNewQuestionText] = useState('');
  const [newQuestionRequired, setNewQuestionRequired] = useState(false);
  const [newQuestionOptions, setNewQuestionOptions] = useState<string[]>(['Option 1', 'Option 2']);
  const [newQuestionCorrectAnswers, setNewQuestionCorrectAnswers] = useState<number[]>([]);
  const [newQuestionScore, setNewQuestionScore] = useState<number>(1);

  // Feedback state
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string; slug?: string } | null>(null);

  // Convert MySQL datetime to datetime-local format
  const convertToDatetimeLocal = (dateString: string | null | undefined): string => {
    if (!dateString) return '';
    try {
      // Handle MySQL datetime format: "2024-02-10 08:00:00"
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      // Convert to YYYY-MM-DDTHH:MM format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (error) {
      console.error('Error converting date:', error);
      return '';
    }
  };

  // Load form data when editing
  useEffect(() => {
    if (currentForm) {
      setFormName(currentForm.name);
      setFormDescription(currentForm.description || '');
      setFormType(currentForm.type);
      setAssessmentType(currentForm.assessmentType || 'objective');
      setShowCorrectAnswers(currentForm.showCorrectAnswers || false);
      setShowReport(currentForm.showReport === true);
      setExpiryDate(convertToDatetimeLocal(currentForm.expiryDate));
      setMaxResponses(currentForm.maxResponses?.toString() || '');
      setOnePerBrowser(currentForm.onePerBrowser);
      setCollectName(currentForm.collectName);
      setCollectEmail(currentForm.collectEmail);
      setQuestions(currentForm.questions.map((q, idx) => ({
        id: q.id?.toString() || `temp-${idx}`,
        type: q.type,
        text: q.text,
        required: q.required,
        options: q.options,
        correctAnswers: q.correctAnswers
      })));
    }
  }, [currentForm]);

  // Auto-hide feedback
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  // Reset questions when changing to assessment type if there are incompatible questions
  useEffect(() => {
    if (formType === 'assessment') {
      let allowedTypes: QuestionType[];
      if (assessmentType === 'both') {
        allowedTypes = ['multiple_choice_single', 'multiple_choice_multi', 'short_text', 'paragraph', 'slider'];
      } else if (assessmentType === 'subjective') {
        allowedTypes = ['short_text', 'paragraph', 'slider'];
      } else {
        allowedTypes = ['multiple_choice_single', 'multiple_choice_multi'];
      }
      const incompatibleQuestions = questions.filter(q => !allowedTypes.includes(q.type));
      if (incompatibleQuestions.length > 0) {
        const typeLabel = assessmentType === 'both' ? 'multiple choice, short text, paragraph, and slider'
          : assessmentType === 'subjective' ? 'short text, paragraph, and slider' : 'multiple choice';
        setFeedback({
          type: 'error',
          message: `${incompatibleQuestions.length} question(s) removed. This assessment can only contain ${typeLabel} questions.`
        });
        setQuestions(questions.filter(q => allowedTypes.includes(q.type)));
      }
    }
  }, [formType, assessmentType]);

  const resetQuestionModal = () => {
    setNewQuestionType('short_text');
    setNewQuestionText('');
    setNewQuestionRequired(false);
    setNewQuestionOptions(['Option 1', 'Option 2']);
    setNewQuestionCorrectAnswers([]);
    setNewQuestionScore(1);
    setEditingQuestion(null);
  };

  const openAddQuestionModal = () => {
    resetQuestionModal();
    setShowAddQuestion(true);
  };

  const openEditQuestionModal = (question: LocalQuestion) => {
    setEditingQuestion(question);
    setNewQuestionType(question.type);
    setNewQuestionText(question.text);
    setNewQuestionRequired(question.required);
    setNewQuestionOptions(question.options || ['Option 1', 'Option 2']);
    setNewQuestionCorrectAnswers(question.correctAnswers || []);
    setNewQuestionScore(question.score || 1);
    setShowAddQuestion(true);
  };

  const handleSaveQuestion = () => {
    if (!newQuestionText.trim()) {
      setFeedback({ type: 'error', message: 'Please enter a question text' });
      return;
    }

    if (newQuestionType.includes('multiple_choice')) {
      const validOptions = newQuestionOptions.filter(o => o && o.trim());
      if (validOptions.length < 2) {
        setFeedback({ type: 'error', message: 'Multiple choice questions must have at least 2 options' });
        return;
      }

      if (formType === 'assessment' && assessmentType !== 'subjective' && newQuestionCorrectAnswers.length === 0) {
        setFeedback({ type: 'error', message: 'Please mark at least one correct answer for assessment questions' });
        return;
      }
    }

    const questionData: LocalQuestion = {
      id: editingQuestion?.id || `temp-${Date.now()}`,
      type: newQuestionType,
      text: newQuestionText.trim(),
      required: newQuestionRequired,
      options: newQuestionType.includes('multiple_choice') ? newQuestionOptions.filter(o => o.trim()) : undefined,
      correctAnswers: (formType === 'assessment' && newQuestionType.includes('multiple_choice')) ? newQuestionCorrectAnswers : undefined,
      score: newQuestionScore,
    };

    if (editingQuestion) {
      setQuestions(questions.map(q => q.id === editingQuestion.id ? questionData : q));
    } else {
      setQuestions([...questions, questionData]);
    }

    setShowAddQuestion(false);
    resetQuestionModal();
  };

  const handleDeleteQuestion = (id: string) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  const handleAddOption = () => {
    setNewQuestionOptions([...newQuestionOptions, `Option ${newQuestionOptions.length + 1}`]);
  };

  const handleUpdateOption = (index: number, value: string) => {
    const updated = [...newQuestionOptions];
    updated[index] = value;
    setNewQuestionOptions(updated);
  };

  const handleRemoveOption = (index: number) => {
    if (newQuestionOptions.length > 2) {
      setNewQuestionOptions(newQuestionOptions.filter((_, i) => i !== index));
      setNewQuestionCorrectAnswers(newQuestionCorrectAnswers.filter(idx => idx !== index).map(idx => idx > index ? idx - 1 : idx));
    }
  };

  const handleToggleCorrectAnswer = (index: number) => {
    if (newQuestionType === 'multiple_choice_single') {
      setNewQuestionCorrectAnswers(newQuestionCorrectAnswers.includes(index) ? [] : [index]);
    } else {
      if (newQuestionCorrectAnswers.includes(index)) {
        setNewQuestionCorrectAnswers(newQuestionCorrectAnswers.filter(idx => idx !== index));
      } else {
        setNewQuestionCorrectAnswers([...newQuestionCorrectAnswers, index]);
      }
    }
  };

  const buildFormData = (status: 'draft' | 'active'): Omit<FeedbackForm, 'id' | 'uuid' | 'slug'> => ({
    name: formName.trim(),
    description: formDescription.trim() || undefined,
    type: formType,
    assessmentType: formType === 'assessment' ? assessmentType : undefined,
    showCorrectAnswers,
    showReport,
    status,
    expiryDate: expiryDate || null,
    maxResponses: maxResponses ? parseInt(maxResponses) : null,
    onePerBrowser,
    collectName,
    collectEmail,
    questions: questions.map(q => ({
      type: q.type,
      text: q.text,
      required: q.required,
      options: q.options,
      correctAnswers: q.correctAnswers,
      score: q.score,
    }))
  });

  const validateForm = (): string | null => {
    if (!formName.trim()) return 'Please enter a survey name';
    if (!formDescription.trim()) return 'Please enter a survey description';
    if (!expiryDate) return 'Please select an expiry date';
    if (questions.length === 0) return 'Please add at least one question';
    return null;
  };

  const handleSaveDraft = async () => {
    const error = validateForm();
    if (error) {
      setFeedback({ type: 'error', message: error });
      return;
    }

    if (!editingFormId) return;

    const formData = buildFormData('draft');
    const result = await updateForm(editingFormId, formData);

    if (result.success) {
      setFeedback({ type: 'success', message: 'Draft saved successfully!' });
    } else {
      setFeedback({ type: 'error', message: result.error || 'Failed to save draft' });
    }
  };

  const handlePublish = async () => {
    const error = validateForm();
    if (error) {
      setFeedback({ type: 'error', message: error });
      return;
    }

    if (!editingFormId) return;

    const formData = buildFormData('active');
    const result = await updateForm(editingFormId, formData);

    if (result.success) {
      setFeedback({ type: 'success', message: 'Survey published successfully!' });
      setTimeout(() => {
        clearCurrentForm();
        onBack();
      }, 2000);
    } else {
      setFeedback({ type: 'error', message: result.error || 'Failed to publish' });
    }
  };

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const questionIds = useMemo(() => questions.map(q => q.id), [questions]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = questions.findIndex(q => q.id === active.id);
    const newIndex = questions.findIndex(q => q.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = [...questions];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    setQuestions(reordered);
  }, [questions]);

  const getTypeLabel = (type: QuestionType): string => {
    return QUESTION_TYPES.find(t => t.value === type)?.label || type;
  };

  const getAvailableQuestionTypes = () => {
    if (formType === 'assessment') {
      if (assessmentType === 'both') {
        return QUESTION_TYPES.filter(t => ['multiple_choice_single', 'multiple_choice_multi', 'short_text', 'paragraph', 'slider'].includes(t.value));
      }
      if (assessmentType === 'subjective') {
        return QUESTION_TYPES.filter(t => t.value === 'short_text' || t.value === 'paragraph' || t.value === 'slider');
      }
      return QUESTION_TYPES.filter(t => t.value === 'multiple_choice_single' || t.value === 'multiple_choice_multi');
    }
    return QUESTION_TYPES;
  };

  const renderPreviewQuestion = (question: LocalQuestion, index: number) => {
    return (
      <div key={question.id} className="admin-feedback-preview-question">
        <div className="admin-feedback-preview-question-label">
          {index + 1}. {question.text} {question.required && <span className="admin-feedback-required">*</span>}
        </div>

        {question.type === 'short_text' && (
          <input type="text" className="admin-feedback-preview-text-input" placeholder="Short answer" readOnly />
        )}

        {question.type === 'paragraph' && (
          <textarea className="admin-feedback-preview-textarea" placeholder="Long answer" rows={3} readOnly />
        )}

        {question.type === 'star_rating' && (
          <div className="admin-feedback-preview-stars">
            {[1, 2, 3, 4, 5].map(star => (
              <Star key={star} size={24} className="admin-feedback-star-icon" />
            ))}
          </div>
        )}

        {question.type === 'multiple_choice_single' && (
          <div className="admin-feedback-preview-options">
            {(question.options || ['Option 1', 'Option 2']).map((opt, i) => (
              <label key={i} className="admin-feedback-preview-option">
                <input type="radio" name={`preview-${question.id}`} disabled />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        )}

        {question.type === 'multiple_choice_multi' && (
          <div className="admin-feedback-preview-options">
            {(question.options || ['Option 1', 'Option 2']).map((opt, i) => (
              <label key={i} className="admin-feedback-preview-option">
                <input type="checkbox" disabled />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="admin-feedback-form-builder">
      {feedback && (
        <div className={`admin-feedback-toast admin-feedback-toast-${feedback.type}`}>
          {feedback.type === 'success' ? <Check size={18} /> : <X size={18} />}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* Back Button */}
      <div className="admin-feedback-edit-header">
        <button className="admin-feedback-back-btn" onClick={onBack}>
          <ArrowLeft size={18} />
          Back to Form Library
        </button>
      </div>

      {/* Loading State */}
      {loading && !currentForm && (
        <LoadingSpinner size="lg" message="Loading form data..." />
      )}

      {/* Error State */}
      {error && !loading && !currentForm && (
        <ErrorDisplay
          title="Failed to load form"
          message={error}
          showRetry={false}
          showHome={true}
          onHome={onBack}
        />
      )}

      {!loading && !error && currentForm && (
      <>
      <div className="admin-feedback-form-builder-layout">
        <div className="admin-feedback-builder-main">
          <div className="admin-feedback-survey-details-section">
            <h3>Edit Survey</h3>

            <div className="admin-feedback-form-field">
              <label htmlFor="survey-name">Survey Name *</label>
              <input
                type="text"
                id="survey-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Course Feedback Survey"
              />
            </div>

            <div className="admin-feedback-form-field">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={3}
                placeholder="Brief description of the survey purpose"
              />
            </div>

            <div className="admin-feedback-form-row">
              <div className="admin-feedback-form-field">
                <label htmlFor="survey-type">Survey Type *</label>
                <select
                  id="survey-type"
                  value={formType}
                  onChange={(e) => setFormType(e.target.value as 'feedback' | 'satisfaction' | 'assessment')}
                >
                  <option value="feedback">Feedback</option>
                  <option value="satisfaction">Satisfaction</option>
                  <option value="assessment">Assessment</option>
                </select>
              </div>

              <div className="admin-feedback-form-field">
                <label htmlFor="expiry-date">Expiry Date *</label>
                <input
                  type="datetime-local"
                  id="expiry-date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                />
              </div>
            </div>

            {formType === 'assessment' && (
              <>
                <div className="admin-feedback-form-row">
                  <div className="admin-feedback-form-field admin-feedback-checkbox-group">
                    <label style={{ marginBottom: '8px', fontWeight: 500 }}>Assessment Type *</label>
                    <label className="admin-feedback-checkbox-label">
                      <input
                        type="checkbox"
                        checked={assessmentType === 'objective' || assessmentType === 'both'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssessmentType(assessmentType === 'subjective' ? 'both' : 'objective');
                          } else {
                            setAssessmentType(assessmentType === 'both' ? 'subjective' : 'subjective');
                          }
                        }}
                      />
                      <span>Objective Assessment</span>
                    </label>
                    <label className="admin-feedback-checkbox-label" style={{ marginTop: '6px' }}>
                      <input
                        type="checkbox"
                        checked={assessmentType === 'subjective' || assessmentType === 'both'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setAssessmentType(assessmentType === 'objective' ? 'both' : 'subjective');
                          } else {
                            setAssessmentType(assessmentType === 'both' ? 'objective' : 'objective');
                          }
                        }}
                      />
                      <span>Subjective Assessment</span>
                    </label>
                  </div>
                </div>

                {assessmentType === 'both' && (
                  <div className="admin-feedback-form-row">
                    <div className="admin-feedback-form-field">
                      <label style={{ marginBottom: '8px', fontWeight: 500 }}>
                        Question Ratio (Objective : Subjective)
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#1976d2', minWidth: '32px', textAlign: 'center' }}>
                          {objectiveRatio}%
                        </span>
                        <input
                          type="range"
                          min={10}
                          max={90}
                          step={10}
                          value={objectiveRatio}
                          onChange={(e) => setObjectiveRatio(parseInt(e.target.value))}
                          style={{ flex: 1 }}
                        />
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#9c27b0', minWidth: '32px', textAlign: 'center' }}>
                          {100 - objectiveRatio}%
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        <span>Objective</span>
                        <span>Subjective</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="admin-feedback-form-row">
                  <div className="admin-feedback-form-field admin-feedback-checkbox-group">
                    <label className="admin-feedback-checkbox-label">
                      <input
                        type="checkbox"
                        checked={showCorrectAnswers}
                        onChange={(e) => setShowCorrectAnswers(e.target.checked)}
                      />
                      <span>Show correct answers after submission</span>
                    </label>
                  </div>
                  <div className="admin-feedback-form-field admin-feedback-checkbox-group">
                    <label className="admin-feedback-checkbox-label">
                      <input
                        type="checkbox"
                        checked={showReport}
                        onChange={(e) => setShowReport(e.target.checked)}
                      />
                      <span>Show report/score upon submission</span>
                    </label>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>
                      When enabled, respondents will see their score report after completing the assessment.
                    </p>
                  </div>
                </div>
              </>
            )}

            <div className="admin-feedback-form-row">
              <div className="admin-feedback-form-field">
                <label htmlFor="max-responses">Max Responses (Optional)</label>
                <input
                  type="number"
                  id="max-responses"
                  value={maxResponses}
                  onChange={(e) => setMaxResponses(e.target.value)}
                  placeholder="Unlimited"
                  min="1"
                />
              </div>

              <div className="admin-feedback-form-field admin-feedback-checkbox-group">
                <label className="admin-feedback-checkbox-label">
                  <input
                    type="checkbox"
                    checked={onePerBrowser}
                    onChange={(e) => setOnePerBrowser(e.target.checked)}
                  />
                  <span>One response per browser</span>
                </label>
              </div>
            </div>

            <div className="admin-feedback-form-row">
              <div className="admin-feedback-form-field admin-feedback-checkbox-group">
                <label className="admin-feedback-checkbox-label">
                  <input
                    type="checkbox"
                    checked={collectName}
                    onChange={(e) => setCollectName(e.target.checked)}
                  />
                  <span>Collect respondent name</span>
                </label>
              </div>

              <div className="admin-feedback-form-field admin-feedback-checkbox-group">
                <label className="admin-feedback-checkbox-label">
                  <input
                    type="checkbox"
                    checked={collectEmail}
                    onChange={(e) => setCollectEmail(e.target.checked)}
                  />
                  <span>Collect respondent email</span>
                </label>
              </div>
            </div>
          </div>

          <div className="admin-feedback-questions-section">
            <div className="admin-feedback-questions-header">
              <h3>Questions ({questions.length})</h3>
              <button className="admin-feedback-add-question-btn" onClick={openAddQuestionModal}>
                <Plus size={16} />
                Add Question
              </button>
            </div>

            <div className="admin-feedback-questions-list">
              {questions.length === 0 ? (
                <div className="admin-feedback-empty-questions">
                  No questions added yet. Click "Add Question" to start building your survey.
                </div>
              ) : (
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={questionIds} strategy={verticalListSortingStrategy}>
                    {questions.map((question, index) => (
                      <SortableEditQuestionItem
                        key={question.id}
                        question={question}
                        index={index}
                        onEdit={openEditQuestionModal}
                        onDelete={handleDeleteQuestion}
                        getTypeLabel={getTypeLabel}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              )}
            </div>

            <div className="admin-feedback-form-actions">
              <button
                className="admin-feedback-form-action-btn admin-feedback-form-action-btn-secondary"
                onClick={handleSaveDraft}
                disabled={saving}
              >
                {saving ? <Loader2 size={16} className="admin-feedback-spinner" /> : null}
                Save as Draft
              </button>
              <button
                className="admin-feedback-form-action-btn admin-feedback-form-action-btn-primary"
                onClick={handlePublish}
                disabled={saving}
              >
                {saving ? <Loader2 size={16} className="admin-feedback-spinner" /> : <span>›</span>}
                Update & Publish
              </button>
            </div>
          </div>
        </div>

        <div className="admin-feedback-builder-preview">
          <div className="admin-feedback-preview-header">
            <h4>Preview</h4>
          </div>
          <div className="admin-feedback-preview-content">
            {formName && <h3 className="admin-feedback-preview-title">{formName}</h3>}
            {formDescription && <p className="admin-feedback-preview-desc">{formDescription}</p>}

            {collectName && (
              <div className="admin-feedback-preview-question">
                <div className="admin-feedback-preview-question-label">Your Name (optional)</div>
                <input type="text" className="admin-feedback-preview-text-input" placeholder="Enter your name" readOnly />
              </div>
            )}

            {collectEmail && (
              <div className="admin-feedback-preview-question">
                <div className="admin-feedback-preview-question-label">Your Email (optional)</div>
                <input type="email" className="admin-feedback-preview-text-input" placeholder="Enter your email" readOnly />
              </div>
            )}

            {questions.map((question, index) => renderPreviewQuestion(question, index))}

            {questions.length === 0 && !collectName && !collectEmail && (
              <div className="admin-feedback-preview-empty">
                Add questions to see preview
              </div>
            )}
          </div>
        </div>
      </div>

      {showAddQuestion && (
        <div className="admin-feedback-modal-overlay" onClick={() => setShowAddQuestion(false)}>
          <div className="admin-feedback-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-feedback-modal-header">
              <h3>{editingQuestion ? 'Edit Question' : 'Add Question'}</h3>
              <button className="admin-feedback-close-btn" onClick={() => setShowAddQuestion(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="admin-feedback-modal-body">
              <div className="admin-feedback-form-field">
                <label>Question Type</label>
                {formType === 'assessment' && (
                  <div className="admin-feedback-info-message" style={{ marginBottom: '8px', padding: '8px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '4px', fontSize: '13px', color: '#92400e' }}>
                    {assessmentType === 'both'
                      ? 'Combined assessments support both multiple choice and text/slider questions'
                      : assessmentType === 'subjective'
                      ? 'Subjective assessment forms can contain short text, paragraph, and slider questions'
                      : 'Objective assessment forms can only contain multiple choice questions'}
                  </div>
                )}
                <div className="admin-feedback-question-type-grid">
                  {getAvailableQuestionTypes().map((type) => (
                    <button
                      key={type.value}
                      className={`admin-feedback-question-type-btn ${newQuestionType === type.value ? 'active' : ''}`}
                      onClick={() => setNewQuestionType(type.value)}
                    >
                      {type.icon}
                      <span>{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="admin-feedback-form-field">
                <label>Question Text *</label>
                <textarea
                  rows={3}
                  value={newQuestionText}
                  onChange={(e) => setNewQuestionText(e.target.value)}
                  placeholder="Enter your question..."
                />
              </div>

              {newQuestionType.includes('multiple_choice') && (
                <div className="admin-feedback-form-field">
                  <label>
                    Options{' '}
                    {formType === 'assessment' && (
                      <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: 'normal' }}>
                        ({newQuestionType === 'multiple_choice_single' ? 'Select ONE correct answer' : 'Check correct answer(s)'})
                      </span>
                    )}
                  </label>
                  <div className="admin-feedback-options-list">
                    {newQuestionOptions.map((option, index) => (
                      <div key={index} className="admin-feedback-option-row">
                        {formType === 'assessment' && (
                          <input
                            type={newQuestionType === 'multiple_choice_single' ? 'radio' : 'checkbox'}
                            name={newQuestionType === 'multiple_choice_single' ? 'correct-answer' : undefined}
                            checked={newQuestionCorrectAnswers.includes(index)}
                            onChange={() => handleToggleCorrectAnswer(index)}
                            style={{ width: 'auto', marginRight: '8px', cursor: 'pointer' }}
                            title="Mark as correct answer"
                          />
                        )}
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => handleUpdateOption(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          style={{ flex: 1 }}
                        />
                        {newQuestionOptions.length > 2 && (
                          <button
                            className="admin-feedback-remove-option-btn"
                            onClick={() => handleRemoveOption(index)}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button className="admin-feedback-add-option-btn" onClick={handleAddOption}>
                      <Plus size={14} /> Add Option
                    </button>
                  </div>
                  {formType === 'assessment' && newQuestionCorrectAnswers.length > 0 && (
                    <div style={{ marginTop: '8px', padding: '8px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '4px', fontSize: '13px', color: '#166534' }}>
                      ✓ {newQuestionCorrectAnswers.length} correct answer{newQuestionCorrectAnswers.length > 1 ? 's' : ''} marked
                    </div>
                  )}
                </div>
              )}

              {formType === 'assessment' && (
                <div className="admin-feedback-form-field admin-feedback-question-score-row">
                  <label>Question Score</label>
                  <input
                    type="number"
                    min={1}
                    value={newQuestionScore}
                    onChange={(e) => setNewQuestionScore(Math.max(1, parseInt(e.target.value) || 1))}
                    style={{ width: '80px' }}
                  />
                </div>
              )}

              <div className="admin-feedback-form-field">
                <label className="admin-feedback-checkbox-label">
                  <input
                    type="checkbox"
                    checked={newQuestionRequired}
                    onChange={(e) => setNewQuestionRequired(e.target.checked)}
                  />
                  <span>Required question</span>
                </label>
              </div>
            </div>

            <div className="admin-feedback-modal-footer">
              <button
                className="admin-feedback-modal-btn admin-feedback-modal-btn-secondary"
                onClick={() => setShowAddQuestion(false)}
              >
                Cancel
              </button>
              <button
                className="admin-feedback-modal-btn admin-feedback-modal-btn-primary"
                onClick={handleSaveQuestion}
                disabled={!newQuestionText.trim()}
              >
                {editingQuestion ? 'Update Question' : 'Add Question'}
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default EditForm;
